import { Fragment, useCallback, useEffect, useMemo } from 'react';
import { __EVENT_ADMIN_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { generatePath, useNavigate } from 'react-router-dom';
import { HttpResponseList } from 'shell/models/http-response';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { useFormik } from 'formik';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { CommonPecCrumbs } from '../constants';
import { headCells } from 'shell/constants/constants-management-pec';
import Table from 'shell/components/shared/table';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { Pec } from 'shell/models/pec';
import { IconButton, Typography } from '@mui/material';
import { noop } from 'shell/utils/noop';
import FiltersPec, {
  validationSchemaFilterPec,
} from 'shell/components/shared/management-pec/pec-filters';
import { FiltersFormPec, initialValuesPec, PecClient } from 'shell/types/types-pec';
import { transformResponseListPec, transformDataset } from 'shell/utils/utils-management-pec';
import { Visibility } from '@mui/icons-material';

const PecListPage = (): React.ReactElement => {
  const filterTable = useFilterTable<FiltersFormPec>();

  const [pec, getPec] = useHttpFetch<HttpResponseList<Pec>, Array<PecClient>>(
    `${__EVENT_ADMIN_API_URL__}/pec/comunicazione/list`,
    {
      onBootstrap: false,
      transformer: transformResponseListPec,
    }
  );

  const pecList = (pec.data as Array<PecClient>) || [];

  const onSubmit = useCallback(
    (formValues: FiltersFormPec) => {
      const queryString = transformDataset({ ...formValues, page: 0 });
      filterPec.setFieldValue('page', 0);
      getPec({ params: {}, paramsSerializer: () => queryString });
    },
    [getPec]
  );

  const filterPec = useFormik<FiltersFormPec>({
    initialValues: initialValuesPec,
    validationSchema: validationSchemaFilterPec,
    onSubmit,
  });

  useEffect(() => {
    if (!filterTable.values?.hasVisitedChild) {
      return;
    }
    filterPec.setValues(filterTable.values || initialValuesPec);
    const queryString = transformDataset(filterTable.values || initialValuesPec);
    getPec({ params: {}, paramsSerializer: () => queryString });
  }, []);

  useEffect(() => {
    return () => filterTable.setValues(filterPec.values);
  }, [filterPec.values]);

  const navigate = useNavigate();

  const onVisibilityPress = useCallback(
    (cell): Promise<PecClient> => {
      return new Promise((resolve) => {
        filterTable.setValues({ hasVisitedChild: true });
        navigate(generatePath('/sistema/pec/dettaglio/:id', { id: cell.code }));
        resolve(cell);
      });
    },
    [navigate, filterTable]
  );

  const handleChangePage = useCallback(
    (newPage) => {
      filterPec.setFieldValue('page', newPage);
    },
    [filterPec.setFieldValue]
  );

  const handleChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = {
        ...filterPec.values,
        rowsPerPage: rowsPerPage || 0,
        page: 0,
      };
      filterPec.setValues(newDataset);
    },
    [filterPec.setValues, filterPec.values]
  );

  const onSort = useCallback(
    (orders) => {
      const newDataset = { ...filterPec.values, orders };
      const queryString = transformDataset(newDataset);
      return (
        getPec({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<Pec>, Array<PecClient>>
      ).then(() => {
        filterPec.setFieldValue('orders', orders);
      });
    },
    [filterPec.setFieldValue, getPec, filterPec.values]
  );

  const body: Array<PecClient> = useMemo(() => {
    return (pecList || []).map((item) => {
      return {
        ...item,
        action: (
          <div className="pmi-d-flex">
            <IconButton
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onVisibilityPress(item);
              }}
              aria-label="Visualizza elemento"
              aria-haspopup="true"
              style={{
                padding: '4px',
              }}
            >
              <Visibility />
            </IconButton>
          </div>
        ),
      };
    });
  }, [pecList]);

  return (
    <Fragment>
      <Breadcrumbs items={CommonPecCrumbs} />
      <div className="pmi-pt-4 pmi-pb-3 pmi-d-flex pmi-align-items-center pmi-justify-content-between">
        <Typography
          color="secondary.main"
          style={{
            fontSize: '26px',
          }}
        >
          Gestione PEC
        </Typography>
      </div>
      <div className="pmi-my-3">
        <FiltersPec filters={filterPec} />
      </div>
      {pec.data ? (
        pecList?.length > 0 ? (
          <Table
            onSort={onSort}
            head={headCells}
            orders={filterPec.values.orders || {}}
            page={filterPec.values.page || 0}
            rowsPerPage={filterPec.values.rowsPerPage}
            body={body}
            noSort
            onVisibilityPress={noop}
            onEditPress={noop}
            onTrashPress={noop}
            count={pecList.length}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ariaLabel="Lista PEC"
            hideButtons
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
    </Fragment>
  );
};

PecListPage.displayName = 'Pec Management list page component';

export default PecListPage;
